import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useSystemLive } from "@/hooks/api/useSystemLive";

function StatCard({ label, value, variant }: { label: string; value: number; variant?: string }) {
  const color =
    variant === "pending" ? "text-admin-caution-400" :
    variant === "running" ? "text-admin-info-400" :
    variant === "completed" ? "text-admin-success-400" :
    variant === "failed" ? "text-admin-danger-400" :
    "text-admin-text-primary";

  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <p className="text-sm text-admin-text-secondary">{label}</p>
        <p className={`text-2xl font-bold ${color}`}>{value.toLocaleString()}</p>
      </CardContent>
    </Card>
  );
}

function ConnectionDot({ connected }: { connected: boolean }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${connected ? "bg-admin-success-500" : "bg-admin-danger-500"}`} />
      <span className={connected ? "text-admin-success-400" : "text-admin-danger-400"}>
        {connected ? "Live" : "Frakoblet"}
      </span>
    </div>
  );
}

function statusBadge(status: string) {
  switch (status) {
    case "running": return <Badge variant="default">Kjorer</Badge>;
    case "pending": return <Badge variant="secondary">Ventende</Badge>;
    case "completed": return <Badge variant="outline">Fullfort</Badge>;
    case "failed": return <Badge variant="destructive">Feilet</Badge>;
    default: return <Badge variant="outline">{status}</Badge>;
  }
}

function formatTimeAgo(isoString: string) {
  const seconds = Math.round((Date.now() - new Date(isoString).getTime()) / 1000);
  if (seconds < 0) return "na";
  if (seconds < 60) return `${seconds}s siden`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min siden`;
  return `${Math.floor(seconds / 3600)}t siden`;
}

function formatUptime(seconds: number) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}t ${m}m`;
  if (h > 0) return `${h}t ${m}m`;
  return `${m}m`;
}

function formatDuration(isoString: string) {
  const ms = new Date(isoString).getTime();
  if (Number.isNaN(ms)) return "na";
  const seconds = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${Math.floor(seconds / 3600)}t ${Math.floor((seconds % 3600) / 60)}m`;
}

function QueueBar({ pending, running, total }: { pending: number; running: number; total: number }) {
  if (total === 0) return null;
  const pct = (v: number) => Math.max(0, Math.min(100, (v / total) * 100));
  return (
    <div className="flex h-2 w-full rounded-full overflow-hidden bg-admin-card-border">
      <div className="bg-admin-info-500 transition-all" style={{ width: `${pct(running)}%` }} />
      <div className="bg-admin-caution-500 transition-all" style={{ width: `${pct(pending)}%` }} />
    </div>
  );
}

export function LiveTab() {
  const live = useSystemLive();

  // Sort job activity: running first, then pending, then completed/failed (fading)
  const statusOrder: Record<string, number> = { running: 0, pending: 1, failed: 2, completed: 3 };
  const sortedJobs = [...live.jobActivity].sort((a, b) => {
    const oa = statusOrder[a.status] ?? 4;
    const ob = statusOrder[b.status] ?? 4;
    if (oa !== ob) return oa - ob;
    return b.count - a.count;
  });

  // Sort API metrics: error rate desc, then total desc
  const sortedMetrics = [...live.apiMetrics].sort((a, b) => {
    if (b.errorRate !== a.errorRate) return b.errorRate - a.errorRate;
    return b.total - a.total;
  });

  return (
    <div className="space-y-6 mt-4">
      {/* Connection + Summary */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Jobbaktivitet</h3>
        <ConnectionDot connected={live.connected} />
      </div>

      {live.jobSummary && (
        <div className="grid grid-cols-4 gap-4">
          <StatCard label="Ventende" value={live.jobSummary.pending} variant="pending" />
          <StatCard label="Kjorer" value={live.jobSummary.running} variant="running" />
          <StatCard label="Fullfort (24t)" value={live.jobSummary.completed} variant="completed" />
          <StatCard label="Feilet (24t)" value={live.jobSummary.failed} variant="failed" />
        </div>
      )}

      {/* Compact Process Health */}
      {live.processStats && (
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-6 text-xs text-admin-text-secondary flex-wrap">
              <span>Oppetid: <strong className="text-admin-text-primary">{formatUptime(live.processStats.uptimeSeconds)}</strong></span>
              <span>Minne: <strong className={live.processStats.system.memoryUsagePercent > 90 ? "text-admin-danger-400" : "text-admin-text-primary"}>{live.processStats.memoryMb.rss}MB</strong> <span className="text-admin-text-subtle">/ {live.processStats.system.totalMemoryMb}MB ({live.processStats.system.memoryUsagePercent}%)</span></span>
              <span>Heap: <strong className="text-admin-text-primary">{live.processStats.memoryMb.heapUsed}MB</strong> <span className="text-admin-text-subtle">/ {live.processStats.memoryMb.heapTotal}MB</span></span>
              <span>Last: <strong className={live.processStats.system.loadAvg[0] > live.processStats.system.cpuCount ? "text-admin-danger-400" : "text-admin-text-primary"}>{live.processStats.system.loadAvg.join(" / ")}</strong></span>
              <span>CPUer: <strong className="text-admin-text-primary">{live.processStats.system.cpuCount}</strong></span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Queue Depth */}
      {live.queueDepth.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Kodybde</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ko</TableHead>
                  <TableHead className="text-right">Ventende</TableHead>
                  <TableHead className="text-right">Kjorer</TableHead>
                  <TableHead className="text-right">Planlagt</TableHead>
                  <TableHead className="w-32"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {live.queueDepth.map((q) => {
                  const total = q.pending + q.running + q.scheduled;
                  return (
                    <TableRow key={q.queue}>
                      <TableCell className="font-mono text-xs">{q.queue}</TableCell>
                      <TableCell className="text-right">
                        {q.pending > 0 ? (
                          <span className="text-admin-caution-400 font-semibold">{q.pending}</span>
                        ) : (
                          <span className="text-admin-text-subtle">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {q.running > 0 ? (
                          <span className="text-admin-info-400 font-semibold">{q.running}</span>
                        ) : (
                          <span className="text-admin-text-subtle">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {q.scheduled > 0 ? (
                          <span className="text-admin-text-secondary">{q.scheduled}</span>
                        ) : (
                          <span className="text-admin-text-subtle">0</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <QueueBar pending={q.pending} running={q.running} total={total} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Long-running jobs */}
      {live.longRunningJobs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Langtlopende jobber
              <Badge variant="secondary">{live.longRunningJobs.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Worker</TableHead>
                  <TableHead>Ko</TableHead>
                  <TableHead>Varighet</TableHead>
                  <TableHead>Forsok</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {live.longRunningJobs.map((j) => {
                  const leaseExpired = j.leaseExpiresAt && new Date(j.leaseExpiresAt) < new Date();
                  return (
                    <TableRow key={j.id} className={leaseExpired ? "bg-admin-danger-500/10" : ""}>
                      <TableCell className="font-mono text-xs">#{j.id}</TableCell>
                      <TableCell className="font-mono text-xs">{j.worker}</TableCell>
                      <TableCell className="text-xs">{j.queue}</TableCell>
                      <TableCell className="text-xs font-mono">
                        {formatDuration(j.startedAt)}
                        {leaseExpired && (
                          <Badge variant="destructive" className="ml-2 text-[10px]">Lease utlopt</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{j.attempts}/{j.maxAttempts}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Active Jobs (worker groups) */}
      <Card>
        <CardHeader><CardTitle>Aktive jobber</CardTitle></CardHeader>
        <CardContent>
          {sortedJobs.length === 0 ? (
            <p className="text-sm text-admin-text-secondary">Ingen aktivitet enna.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Worker</TableHead>
                  <TableHead className="text-right">Antall</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sist</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedJobs.map((g) => {
                  const key = `${g.worker}-${g.status}`;
                  const fadeAt = live.fadingJobs.get(key);
                  const isFading = fadeAt !== undefined;
                  const opacity = isFading
                    ? Math.max(0, (fadeAt - Date.now()) / 3000)
                    : 1;

                  return (
                    <TableRow
                      key={key}
                      style={{ opacity, transition: "opacity 0.5s ease-out" }}
                    >
                      <TableCell className="font-mono text-xs">{g.worker}</TableCell>
                      <TableCell className="text-right">{g.count}x</TableCell>
                      <TableCell>{statusBadge(g.status)}</TableCell>
                      <TableCell className="text-xs text-admin-text-secondary">
                        {formatTimeAgo(g.latestAt)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* API Traffic */}
      <Card>
        <CardHeader>
          <CardTitle>API-trafikk (5 min)</CardTitle>
        </CardHeader>
        <CardContent>
          {live.apiTotals && (
            <div className="flex gap-4 mb-4 text-xs text-admin-text-secondary">
              <span>Totalt: <strong className="text-admin-text-primary">{live.apiTotals.total}</strong></span>
              <span>2xx: <strong className="text-admin-success-400">{live.apiTotals["2xx"]}</strong></span>
              <span>4xx: <strong className="text-admin-caution-400">{live.apiTotals["4xx"]}</strong></span>
              <span>5xx: <strong className="text-admin-danger-400">{live.apiTotals["5xx"]}</strong></span>
              <span>Feilrate: <strong className={live.apiTotals.errorRate > 5 ? "text-admin-danger-400" : "text-admin-text-primary"}>{live.apiTotals.errorRate}%</strong></span>
            </div>
          )}
          {sortedMetrics.length === 0 ? (
            <p className="text-sm text-admin-text-secondary">Ingen API-data enna.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>Metode</TableHead>
                  <TableHead className="text-right">Totalt</TableHead>
                  <TableHead className="text-right">2xx</TableHead>
                  <TableHead className="text-right">4xx</TableHead>
                  <TableHead className="text-right">5xx</TableHead>
                  <TableHead className="text-right">Feilrate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedMetrics.map((e) => (
                  <TableRow
                    key={`${e.method}-${e.path}`}
                    className={e.errorRate > 5 ? "bg-admin-danger-500/10" : ""}
                  >
                    <TableCell className="font-mono text-xs">{e.path}</TableCell>
                    <TableCell className="text-xs">{e.method}</TableCell>
                    <TableCell className="text-right">{e.total}</TableCell>
                    <TableCell className="text-right text-admin-success-400">{e["2xx"]}</TableCell>
                    <TableCell className="text-right text-admin-caution-400">{e["4xx"]}</TableCell>
                    <TableCell className="text-right text-admin-danger-400">{e["5xx"]}</TableCell>
                    <TableCell className={`text-right ${e.errorRate > 5 ? "text-admin-danger-400 font-semibold" : ""}`}>
                      {e.errorRate}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Error Patterns */}
      <Card>
        <CardHeader><CardTitle>Feilmonstre (siste time)</CardTitle></CardHeader>
        <CardContent>
          {live.errorPatterns.length === 0 ? (
            <p className="text-sm text-admin-text-secondary">Ingen feil registrert.</p>
          ) : (
            <div className="space-y-3">
              {live.errorPatterns.map((p) => (
                <div key={`${p.context}:${p.pattern}`} className="border border-admin-card-border rounded-lg p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="destructive">{p.count}x</Badge>
                        <span className="text-xs font-mono text-admin-text-secondary">[{p.context}]</span>
                      </div>
                      <p className="text-sm font-mono text-admin-text-primary truncate">{p.pattern}</p>
                    </div>
                  </div>
                  <p className="text-xs text-admin-text-subtle mt-1">
                    Siste: {formatTimeAgo(p.latestAt)} | Forste: {formatTimeAgo(p.firstAt)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Failed Jobs */}
      {live.recentFailures.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Siste feilede jobber
              <Badge variant="destructive">{live.recentFailures.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Worker</TableHead>
                  <TableHead>Ko</TableHead>
                  <TableHead>Feil</TableHead>
                  <TableHead>Forsok</TableHead>
                  <TableHead>Tidspunkt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {live.recentFailures.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="font-mono text-xs">#{f.id}</TableCell>
                    <TableCell className="font-mono text-xs">{f.worker}</TableCell>
                    <TableCell className="text-xs">{f.queue}</TableCell>
                    <TableCell className="text-xs text-admin-danger-400 max-w-xs truncate" title={f.error ?? undefined}>
                      {f.error ? f.error.slice(0, 80) : "-"}
                      {f.error && f.error.length > 80 ? "..." : ""}
                    </TableCell>
                    <TableCell className="text-xs">{f.attempts}/{f.maxAttempts}</TableCell>
                    <TableCell className="text-xs text-admin-text-secondary">
                      {formatTimeAgo(f.failedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
